import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Mail, MessageSquare, Linkedin, Send, CheckCircle } from 'lucide-react';
import Card3D from '../components/Card3D';
import NavigationExact from '../components/NavigationExact';
import FooterExact from '../components/FooterExact';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = '/server/php-api/contact.php';
      const params = new URLSearchParams();
      params.append('fullName', formData.name);
      params.append('email', formData.email);
      params.append('phone', formData.phone);
      params.append('message', formData.message);

      const response = await fetch(url, {
        method: 'POST',
        body: params
      });
      // parse response with JSON fallback
      let json = null;
      try {
        json = await response.json();
      } catch (parseErr) {
        const text = await response.text().catch(() => '');
        json = { message: text || response.statusText || 'Server error' };
      }

      if (!response.ok) {
        throw new Error(json.message || 'Failed to send message');
      }

      setErrorMsg('');
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error('Failed to send contact form:', err);
      setErrorMsg(err.message || 'Failed to send message');
      setIsSubmitted(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Office Address',
      details: ['GA-14, Lake City Mall, Near Goenka International School', 'Kapurbawadi, Thane West 400607', 'Maharashtra, India']
    },
    {
      icon: Phone,
      title: 'Phone',
      details: ['+91 22 4815 2124', '+91 91104 51232']
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['info@fundingsathi.com', 'support@fundingsathi.com'],
      links: ['mailto:info@fundingsathi.com', 'mailto:support@fundingsathi.com']
    },
    {
      icon: Linkedin,
      title: 'LinkedIn',
      details: ['linkedin.com/company/fundingsathi.com'],
      links: ['https://www.linkedin.com/company/fundingsathi.com']
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday: 10:00 AM - 2:00 PM']
    }
  ];

  return (
    <>
      <NavigationExact />
      <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-red-50/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Contact Us
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-gray-600 text-lg"
          >
            Whether you're an anchor corporate, an MSME supplier, or an institutional capital partner — tell us what you're working on and we'll connect you to the right team.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#5C1F33]" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">{item.title}</h4>
                    {item.details.map((detail, i) => (
                      item.links && item.links[i] ? (
                        <a key={i} href={item.links[i]} target="_blank" rel="noreferrer" className="text-sm text-[#5C1F33] hover:underline block">
                          {detail}
                        </a>
                      ) : (
                        <p key={i} className="text-sm text-gray-600">{detail}</p>
                      )
                    ))}
                  </div>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="rounded-3xl overflow-hidden border border-gray-200 bg-white"
              >
                <div className="px-5 py-4 border-b border-gray-100">
                  <h4 className="text-lg font-semibold">Our Location</h4>
                  <p className="text-sm text-gray-500">GA-14, Lake City Mall, Kapurbawadi, Thane West 400607</p>
                </div>
                <div className="h-72 min-h-[18rem]">
                  <iframe
                    src="https://maps.google.com/maps?q=Lake+City+Mall%2C+Thane%2C+Maharashtra+400601&z=16&output=embed"
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Funding Sathi Location"
                  />
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <Card3D className="h-full w-full">
                <div className="bg-gray-50 rounded-2xl p-8 w-full">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
                      <p className="text-gray-600">Your message has been sent. We'll get back to you soon.</p>
                    </div>
                  ) : (
                <>
                  {errorMsg && (
                    <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                      {errorMsg}
                    </div>
                  )}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name <span className="text-[#5C1F33]">*</span></label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-white focus:border-[#5C1F33] focus:ring-2 focus:ring-[#5C1F33]/10 focus:outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address <span className="text-[#5C1F33]">*</span></label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-white focus:border-[#5C1F33] focus:ring-2 focus:ring-[#5C1F33]/10 focus:outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number <span className="text-[#5C1F33]">*</span></label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-white focus:border-[#5C1F33] focus:ring-2 focus:ring-[#5C1F33]/10 focus:outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-white focus:border-[#5C1F33] focus:ring-2 focus:ring-[#5C1F33]/10 focus:outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                        placeholder="Your Company Ltd."
                      />
                    </div>
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-white focus:border-[#5C1F33] focus:ring-2 focus:ring-[#5C1F33]/10 focus:outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300 resize-none"
                      placeholder="E.g. I supply to a Tier-1 auto company and have ₹40L in unpaid invoices. How quickly can I get funded?"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#5C1F33] to-[#800020] text-white px-10 py-4 rounded-xl hover:shadow-lg hover:shadow-[#5C1F33]/25 transition-all duration-300 font-semibold text-base group"
                  >
                    <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    Send Message
                  </button>
                </form>
                </>
              )}
                </div>
              </Card3D>
            </motion.div>
          </div>
        </div>
      </section>

      <FooterExact />
    </div>
    </>
  );
};

export default ContactPage;
